<template>
  <Layout>
    <div class="mc-list-header">
      <PageHeader :title="title" :subTitle="subTitle">
        <template #afterTitle>
          <McButton
            variant="secondary"
            class="ms-3"
            :text="$t('global.back')"
            icon="icon-outline_arrow-left"
            @click="$router.push({ name: 'Master Home Page' })"
          />
        </template>
      </PageHeader>

      <McTable
        v-show="isTableDataAvailable"
        withCard
        searchable
        :hasDelete="isBulkDelete ? false : true"
        stickyHeader
        :fields="fields"
        :apiClient="apiClient"
        :customHandlers="{
          handleClickRow,
          handleAdd,
        }"
        hasEmit:deleteSuccess
        hasEmit:deleteError
        :bulkDeleteApi="isBulkDelete"
        @deleteSuccess="handleDeleteSuccess"
        @deleteError="handleDeleteError"
        @listRes="handleListResponse"
        @available-change="handleDataAvailable"
      >
        <template #before-table="{ isBusy, isSelected, showDeleteModal }">
          <div class="d-flex gap-2 justify-content-end mb-4">
            <McButton
              variant="secondary"
              icon="icon-outline_upload"
              :text="$t('global.action.upload')"
              @click="$router.push('/master/product/import')"
            />
            <McButton
              v-if="isSelected"
              variant="secondary-red"
              icon="icon-outline_trash"
              :text="$t('global.delete')"
              @click="showDeleteModal"
            />
            <McButton
              v-if="hasData"
              variant="primary"
              icon="icon-fill_plus"
              :disabled="isBusy"
              :text="$t('global.action.create')"
              @click="handleAdd"
            />
          </div>
        </template>
        <template #[`cell(product_type.product_category.box_types)`]="{ item }">
          <McChipList
            v-if="item?.product_type?.product_category?.box_types && item?.product_type?.product_category?.box_types?.length"
            :items="item.product_type.product_category.box_types"
            :max-visible="3"
            itemTextField="name"
            showAllMode="dropdown"
            :showAllDropdownTitle="$t('setting.list.product.field.boxType')"
            showAllDropdownTrigger="action"
          />
          <span v-else>-</span>
        </template>
      </McTable>
      <b-card v-show="!isTableDataAvailable">
        <div class="empty-table-container">
          <img :src="illustrationPath" alt />
          <strong>{{ $t('master.product.noData') }}</strong>
          <p>{{ $t('master.product.addEmpty') }}</p>
          <McButton
            variant="primary"
            :text="$t('global.addField')"
            icon="icon-outline_plus"
            @click="handleAdd"
          />
        </div>
      </b-card>
    </div>
  </Layout>
</template>

<script>
import appConfig from '@/app.config'
import PageHeader from '@/components/page-header-v2'
import Layout from '@/router/layouts/main'
import McTable from '@/components/McTable'
import McButton from '@/components/McButton'
import McChipList from '@/components/McChipList'
import * as Product from '@/services/rest-api/lib/product/product'
import { mapGetters } from 'vuex'

/**
 * Starter component
 */
export default {
  page: {
    title: 'Master Product',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    Layout,
    PageHeader,
    McTable,
    McButton,
    McChipList,
  },
  data() {
    return {
      hasData: true,
      isTableDataAvailable: true,
    }
  },
  computed: {
    title() {
      return this.$t('master.product.title')
    },
    subTitle() {
      return this.$t('master.product.list')
    },
    apiClient() {
      return {
        get: Product.get,
        delete: this.isBulkDelete ? Product.bulkDelete : Product.deleteSingle,
      }
    },
    fields() {
      const fields = [
        {
          label: this.$t('master.product.name'),
          key: 'name',
          isSortable: true,
          isRedirectable: true,
          tdStyle: 'display:inline-block; width: 230px;',
          columnTooltipLength: 30,
          columnHasTooltip: true,
        },
        {
          label: this.$t('master.product.sku'),
          key: 'sku',
          isSortable: true,
        },
        {
          label: this.$t('master.product.productType'),
          key: 'product_type.name',
          isSortable: true,
        },
        {
          label: this.$t('master.product.productCategory'),
          key: 'product_type.product_category.name',
          isSortable: true,
        },
        {
          label: this.$t('master.product.minTemperature'),
          key: 'product_type.product_category.min_temperature',
          isSortable: true,
          formatter: (value) => ((value ?? '') === '' ? '-' : `${value}`),
        },
        {
          label: this.$t('master.product.maxTemperature'),
          key: 'product_type.product_category.max_temperature',
          isSortable: true,
          formatter: (value) => ((value ?? '') === '' ? '-' : `${value}`),
        },
        {
          label: this.$t('master.product.productWeight'),
          key: 'weight',
          isSortable: true,
        },
        {
          label: this.$t('master.product.baseUnit'),
          key: 'uom',
          isSortable: true,
        },
        {
          label: this.$t('master.product.boxType'),
          key: 'product_type.product_category.box_types',
          isSortable: false,
          tdAttr: {
            style: 'position:relative;min-width:300px;max-width:300px',
          },
          thAttr: {
            style: 'min-width:350px;',
          },
        },
      ]
      return fields
    },
    ...mapGetters({
      isWhitelabel: 'identity/isWhitelabel',
      isMetrack: 'identity/isMetrack',
      getFeatureFlagValue: 'identity/getFeatureFlagValue',
    }),
    illustrationPath() {
      if (this.isWhitelabel() || this.isMetrack()) {
        return require(`@/assets/images/illustration/whitelabel/no-data.svg`)
      }
      return require(`@/assets/images/illustration/saas/no-data.svg`)
    },
    isBulkDelete() {
      return this.getFeatureFlagValue('setTransitionQAandProductFlag')
    },
  },
  methods: {
    handleClickRow(item) {
      this.$router.push({ name: 'Master Product Update', params: { id: item.id } })
    },
    handleAdd() {
      this.$router.push({ name: 'Master Product Create' })
    },
    handleListResponse(response) {
      this.hasData = response?.data?.paginated_result?.length > 0
    },
    handleDataAvailable(isAvailable) {
      this.isTableDataAvailable = isAvailable
    },
    handleDeleteSuccess(data) {
      if (!this.isBulkDelete) return

      let success = data?.data?.success
      let failed = data?.data?.failed

      if (success > 0) {
        this.$mcToast.success({
          component: 'McToastDynamicBody',
          props: {
            contents: [
              {
                tag: 'strong',
                text: success,
              },
              { tag: 'span', text: this.$t('global.alert.text.delete') },
            ],
          },
        })
      }

      if (failed > 0) {
        this.$mcToast.success({
          component: 'McToastDynamicBody',
          props: {
            contents: [
              {
                tag: 'strong',
                text: success,
              },
              { tag: 'span', text: this.$t('global.alert.text.failDelete') },
            ],
          },
        })
      }
    },
    handleDeleteError(data) {
      if (!this.isBulkDelete) return

      this.$mcToast.error({
        component: 'McToastDynamicBody',
        props: {
          contents: [
            {
              tag: 'strong',
              text: data?.data?.failed,
            },
            { tag: 'span', text: this.$t('global.alert.text.failDelete') },
          ],
        },
      })
    },
  },
}
</script>
